import React, { useState, useEffect, useRef, useCallback } from 'react';
import { getRooms, getTables, updateTablePosition, updateTable } from '../../../lib/api';
import { Button } from '../../../components/ui/button';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../../components/ui/select';
import { RefreshCw, Loader2, Save, Table2, Move, Info, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

const GRID_SIZE = 20;      // px per grid unit
const TABLE_W = 80;        // default table width px
const TABLE_H = 60;        // default table height px
const TABLE_R = 40;        // round table radius px (diameter = 80)
const CANVAS_W = 1200;
const CANVAS_H = 800;

function snap(val) {
  return Math.round(val / GRID_SIZE) * GRID_SIZE;
}

function TableShape({ table, selected, onSelect, onDragStart }) {
  const isRound = table.shape === 'round';
  const isOccupied = table.status === 'occupied';

  const baseStyle = {
    position: 'absolute',
    left: table.position_x,
    top: table.position_y,
    width: isRound ? TABLE_R * 2 : TABLE_W,
    height: isRound ? TABLE_R * 2 : TABLE_H,
    cursor: 'grab',
    userSelect: 'none',
    zIndex: selected ? 20 : 10,
    transition: 'box-shadow 0.15s',
  };

  const colorClass = isOccupied
    ? 'bg-red-100 border-red-400 text-red-700'
    : 'bg-emerald-50 border-emerald-400 text-emerald-700';

  const selectedRing = selected ? 'ring-2 ring-indigo-500 ring-offset-1' : '';

  return (
    <div
      style={baseStyle}
      className={`flex flex-col items-center justify-center border-2 shadow-md select-none
        ${colorClass} ${selectedRing}
        ${isRound ? 'rounded-full' : 'rounded-xl'}`}
      onMouseDown={(e) => {
        e.preventDefault();
        onSelect(table.id);
        onDragStart(e, table);
      }}
      title={`${table.name} · ${table.capacity} Plätze · ${isOccupied ? 'Besetzt' : 'Frei'}`}
    >
      <span className="text-[11px] font-bold leading-tight text-center px-1 truncate w-full text-center">
        {table.name}
      </span>
      <span className="text-[10px] opacity-60">{table.capacity}P</span>
    </div>
  );
}

export default function AdminRoomLayout() {
  const [rooms, setRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState('');
  const [tables, setTables] = useState([]);
  const [pendingPositions, setPendingPositions] = useState({}); // id → {x, y}
  const [selectedTableId, setSelectedTableId] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const canvasRef = useRef(null);
  const dragRef = useRef(null); // { tableId, startMouseX, startMouseY, startTableX, startTableY }

  useEffect(() => {
    loadRooms();
  }, []);

  useEffect(() => {
    if (selectedRoomId) loadTables(selectedRoomId);
  }, [selectedRoomId]);

  const loadRooms = async () => {
    try {
      const data = await getRooms();
      setRooms(data);
      if (data.length > 0) setSelectedRoomId(data[0].id);
    } catch {
      toast.error('Fehler beim Laden der Räume');
    } finally {
      setIsLoading(false);
    }
  };

  const loadTables = async (roomId) => {
    try {
      const data = await getTables(roomId);
      setTables(data);
      setPendingPositions({});
      setSelectedTableId(null);
    } catch {
      toast.error('Fehler beim Laden der Tische');
    }
  };

  // Compute displayed tables with pending overrides
  const displayedTables = tables.map(t => ({
    ...t,
    position_x: pendingPositions[t.id]?.x ?? t.position_x,
    position_y: pendingPositions[t.id]?.y ?? t.position_y,
  }));

  const hasPending = Object.keys(pendingPositions).length > 0;

  // ── Drag handling ──────────────────────────────────────────────────────────
  const handleDragStart = useCallback((e, table) => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    dragRef.current = {
      tableId: table.id,
      startMouseX: e.clientX,
      startMouseY: e.clientY,
      startTableX: pendingPositions[table.id]?.x ?? table.position_x,
      startTableY: pendingPositions[table.id]?.y ?? table.position_y,
      canvasLeft: rect.left,
      canvasTop: rect.top,
    };
  }, [pendingPositions]);

  useEffect(() => {
    const onMouseMove = (e) => {
      if (!dragRef.current) return;
      const { tableId, startMouseX, startMouseY, startTableX, startTableY } = dragRef.current;
      const dx = e.clientX - startMouseX;
      const dy = e.clientY - startMouseY;
      const newX = Math.max(0, Math.min(CANVAS_W - TABLE_W, snap(startTableX + dx)));
      const newY = Math.max(0, Math.min(CANVAS_H - TABLE_H, snap(startTableY + dy)));
      setPendingPositions(prev => ({ ...prev, [tableId]: { x: newX, y: newY } }));
    };

    const onMouseUp = () => {
      dragRef.current = null;
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  // ── Save positions ─────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!hasPending) return;
    setIsSaving(true);
    try {
      await Promise.all(
        Object.entries(pendingPositions).map(([id, { x, y }]) =>
          updateTablePosition(id, x, y)
        )
      );
      // Merge into local state
      setTables(prev =>
        prev.map(t =>
          pendingPositions[t.id]
            ? { ...t, position_x: pendingPositions[t.id].x, position_y: pendingPositions[t.id].y }
            : t
        )
      );
      setPendingPositions({});
      toast.success('Raumplan gespeichert');
    } catch {
      toast.error('Fehler beim Speichern');
    } finally {
      setIsSaving(false);
    }
  };

  const selectedTable = displayedTables.find(t => t.id === selectedTableId);
  const currentRoom = rooms.find(r => r.id === selectedRoomId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-[#1a2f38]" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#1a2f38]">Raumplan</h1>
          <p className="text-slate-500">Tische per Drag & Drop auf dem Raumplan platzieren</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            onClick={() => selectedRoomId && loadTables(selectedRoomId)}
          >
            <RefreshCw className="w-4 h-4 mr-2" />
            Zurücksetzen
          </Button>
          <Button
            className="bg-[#1a2f38] hover:bg-[#2a4048]"
            onClick={handleSave}
            disabled={!hasPending || isSaving}
          >
            {isSaving
              ? <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              : <Save className="w-4 h-4 mr-2" />
            }
            Speichern{hasPending ? ` (${Object.keys(pendingPositions).length})` : ''}
          </Button>
        </div>
      </div>

      {/* Controls row */}
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-slate-600">Raum:</span>
          <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Raum wählen" />
            </SelectTrigger>
            <SelectContent>
              {rooms.map(r => (
                <SelectItem key={r.id} value={r.id}>{r.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Legend */}
        <div className="flex items-center gap-3 text-xs text-slate-500 ml-4">
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-emerald-50 border-2 border-emerald-400 rounded-sm" />
            Frei
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-red-100 border-2 border-red-400 rounded-sm" />
            Besetzt
          </div>
          <div className="flex items-center gap-1">
            <div className="w-4 h-4 bg-indigo-100 border-2 border-indigo-500 rounded-sm ring-2 ring-indigo-500 ring-offset-1" />
            Ausgewählt
          </div>
        </div>

        {hasPending && (
          <div className="flex items-center gap-1.5 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-1.5">
            <Info className="w-3.5 h-3.5" />
            Ungespeicherte Änderungen
          </div>
        )}
      </div>

      {/* Info bar + Selected table */}
      {selectedTable && (
        <div className="flex items-center gap-3 bg-indigo-50 border border-indigo-200 rounded-xl px-4 py-3 text-sm">
          <Table2 className="w-4 h-4 text-indigo-600 shrink-0" />
          <span className="font-medium text-indigo-800">{selectedTable.name}</span>
          <span className="text-indigo-600">{selectedTable.capacity} Plätze · {selectedTable.shape === 'round' ? 'Rund' : 'Eckig'}</span>
          <span className="text-indigo-400 font-mono text-xs">
            x:{pendingPositions[selectedTable.id]?.x ?? selectedTable.position_x} y:{pendingPositions[selectedTable.id]?.y ?? selectedTable.position_y}
          </span>
          <span className={`ml-auto px-2 py-0.5 rounded-full text-xs font-medium ${
            selectedTable.status === 'occupied' ? 'bg-red-100 text-red-700' : 'bg-emerald-100 text-emerald-700'
          }`}>
            {selectedTable.status === 'occupied' ? 'Besetzt' : 'Frei'}
          </span>
        </div>
      )}

      {/* Canvas */}
      <div className="border border-slate-200 rounded-2xl overflow-hidden shadow-sm bg-white">
        <div
          ref={canvasRef}
          className="relative overflow-auto"
          style={{
            width: '100%',
            height: 600,
            backgroundImage: `
              linear-gradient(to right, #f1f5f9 1px, transparent 1px),
              linear-gradient(to bottom, #f1f5f9 1px, transparent 1px)
            `,
            backgroundSize: `${GRID_SIZE}px ${GRID_SIZE}px`,
            minWidth: CANVAS_W,
            minHeight: CANVAS_H,
            cursor: dragRef.current ? 'grabbing' : 'default',
          }}
          onClick={(e) => {
            if (e.target === canvasRef.current) setSelectedTableId(null);
          }}
        >
          {displayedTables.length === 0 && (
            <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-300 pointer-events-none">
              <Table2 className="w-16 h-16 mb-3" />
              <p className="text-lg font-medium">Keine Tische in diesem Raum</p>
              <p className="text-sm">Tische unter Standorte → Tische hinzufügen</p>
            </div>
          )}

          {displayedTables.map(table => (
            <TableShape
              key={table.id}
              table={table}
              selected={table.id === selectedTableId}
              onSelect={setSelectedTableId}
              onDragStart={handleDragStart}
            />
          ))}
        </div>
      </div>

      {/* Footer summary */}
      <div className="flex items-center gap-4 text-sm text-slate-500">
        <span className="flex items-center gap-1.5">
          <Move className="w-4 h-4" />
          Tisch ziehen zum Verschieben · Klicken zum Auswählen
        </span>
        <span className="ml-auto">
          {currentRoom?.name} · {displayedTables.length} Tische
        </span>
      </div>
    </div>
  );
}
